import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, Label, Input, Button, Form } from 'reactstrap';
import { CoreSkola24ScheduleOrganizationExportConfiguration } from '../../../../../../models/CoreSkola24ScheduleConfigurationModels';
import FontAwesomeButton from '../../../../../misc/FontAwesomeButton';
import { GridListHeader, GridListItem } from '../../../../../misc/GridListComponents';
import ModalComponent from '../../../../../misc/ModalComponent';

interface DomainNameFilterState {
    currentIndex?: number
    currentFilter: string
    removingIndex?: number
    removingFilter?: string
}

interface Skola24ScheduleOrganizationExportProps {
    organizationExport: CoreSkola24ScheduleOrganizationExportConfiguration
    onChange: (update: CoreSkola24ScheduleOrganizationExportConfiguration) => void
    disabled?: boolean
}

const Skola24ScheduleOrganizationExport: React.FC<Skola24ScheduleOrganizationExportProps> = (props: Skola24ScheduleOrganizationExportProps) => {
    const { t } = useTranslation(['common', 'configuration']);
    const [organizationExportInput, setOrganizationExportInput] = useState<CoreSkola24ScheduleOrganizationExportConfiguration>({ ...props.organizationExport })
    const [domainNameFilterState, setDomainNameFilterState] = useState<DomainNameFilterState>({ currentFilter: '' })

    const update = (update: CoreSkola24ScheduleOrganizationExportConfiguration) => {
        setOrganizationExportInput(update);
        props.onChange(update);
    }

    const updateCurrentDomainNameFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDomainNameFilterState({ ...domainNameFilterState, currentFilter: e.target.value })
    }

    const submitDomainNameFilter = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (domainNameFilterState.currentFilter) {
            const filters = calculateUpdatedFilters();
            update({ ...organizationExportInput, domainNameFilter: filters });
            setDomainNameFilterState({ currentFilter: '' });
        }
    }

    const editDomainNameFilter = (index: number) => {
        setDomainNameFilterState({
            currentIndex: index,
            currentFilter: organizationExportInput.domainNameFilter[index],
        });
    }

    const beginRemoveDomainNameFilter = (index: number) => {
        setDomainNameFilterState({
            currentFilter: '',
            removingFilter: organizationExportInput.domainNameFilter[index],
            removingIndex: index,
        });
    }

    const endRemoveDomainNameFilter = (e: React.MouseEvent<HTMLButtonElement>) => {
        const filters = organizationExportInput.domainNameFilter.filter((_, i) => i !== domainNameFilterState.removingIndex);
        update({ ...organizationExportInput, domainNameFilter: filters });
        setDomainNameFilterState({ currentFilter: '' });
    }

    const abort = () => {
        setDomainNameFilterState({
            currentIndex: undefined,
            currentFilter: '',
            removingIndex: undefined,
        });
    }

    const calculateUpdatedFilters = () => {
        if (Number.isFinite(domainNameFilterState.currentIndex)) {
            return organizationExportInput.domainNameFilter.map((x, i) => i === domainNameFilterState.currentIndex ? domainNameFilterState.currentFilter : x);
        } else {
            return [...organizationExportInput.domainNameFilter, domainNameFilterState.currentFilter];
        }
    }

    const isDomainNameFilterValid = () => domainNameFilterState.currentFilter && domainNameFilterState.currentFilter.length > 0;

    const renderDomainNameFilter = () => {
        return (
            <React.Fragment>
                <h5>{t('skola24Schedule.organizationExport.domainNameFilter.title', { ns: 'configuration' })}</h5>
                <p>{t('skola24Schedule.organizationExport.domainNameFilter.description', { ns: 'configuration' })}</p>
                <Form className="gridList" style={{ gridTemplateColumns: '16fr 1.5fr 1.5fr' }} onSubmit={submitDomainNameFilter}>
                    <GridListHeader>{t('skola24Schedule.organizationExport.domainNameFilter.header1', { ns: 'configuration' })}</GridListHeader>
                    <GridListHeader span={{ startColumn: 2, columnCount: 2 }} />
                    {organizationExportInput.domainNameFilter.map((filter, index) => renderDomainNameFilterRow(filter, index))}
                    {renderDomainNameFilterRow(domainNameFilterState.currentFilter)}
                </Form>
                {Number.isFinite(domainNameFilterState.removingIndex) && renderConfirmDeleteModal()}
            </React.Fragment>);
    }

    const renderDomainNameFilterRow = (filter: string, index?: number) => {
        const editing = Number.isFinite(domainNameFilterState.currentIndex);
        if (index === domainNameFilterState.currentIndex) {
            return (<React.Fragment key={index}>
                <GridListItem>
                    <Input type="text" disabled={props.disabled} placeholder={t('skola24Schedule.organizationExport.domainNameFilter.placeholder', { ns: 'configuration' })} autoComplete="off" value={domainNameFilterState.currentFilter} onChange={updateCurrentDomainNameFilter} />
                </GridListItem>
                {editing ? <GridListItem span={{ startColumn: 2, columnCount: 2 }}>
                    <Button style={{ width: '100%' }} color="primary" type="submit" disabled={props.disabled || !isDomainNameFilterValid()}>{t('action.save', { ns: 'common' })}</Button>
                    <Button style={{ width: '100%' }} color="danger" onClick={() => abort()}>{t('action.cancel', { ns: 'common' })}</Button>
                </GridListItem> : <GridListItem span={{ startColumn: 2, columnCount: 2 }}>
                    <Button style={{ width: '100%' }} color="primary" type="submit" disabled={props.disabled || !isDomainNameFilterValid()}>{t('action.add', { ns: 'common' })}</Button>
                </GridListItem>}
            </React.Fragment>)
        } else if (index !== undefined) {
            return (<React.Fragment key={index}>
                <GridListItem>{filter}</GridListItem>
                <GridListItem className="gridListCenterContent">
                    <FontAwesomeButton icon="pen" onClick={() => editDomainNameFilter(index)} disabled={props.disabled || editing} title={t('skola24Schedule.organizationExport.domainNameFilter.editTitle', { ns: 'configuration' })} />
                </GridListItem>
                <GridListItem className="gridListCenterContent">
                    <FontAwesomeButton icon="trash" onClick={() => beginRemoveDomainNameFilter(index)} disabled={props.disabled || editing} title={t('skola24Schedule.organizationExport.domainNameFilter.removeTitle', { ns: 'configuration' })} />
                </GridListItem>
            </React.Fragment>);
        } else {
            return null;
        }
    }

    const renderConfirmDeleteModal = () => {
        return (
            <ModalComponent
                isOpen={Number.isFinite(domainNameFilterState.removingIndex)}
                header={t('skola24Schedule.organizationExport.domainNameFilter.removeModalHeader', { ns: 'configuration' })}
                disabled={props.disabled}
                confirm={t('yes', { ns: 'common' })}
                cancel={t('no', { ns: 'common' })}
                toggleModal={(e) => setDomainNameFilterState({ currentIndex: undefined, currentFilter: '', removingIndex: undefined })}
                update={endRemoveDomainNameFilter}
            >
                <div>
                    <p>{t('skola24Schedule.organizationExport.domainNameFilter.removeModalText', { ns: 'configuration', domainName: domainNameFilterState.removingFilter })} </p>
                </div>
            </ModalComponent>
        )
    }

    return <FormGroup>
        <h5>{t('skola24Schedule.organizationExport.title', { ns: 'configuration' })} </h5>
        <p>{t('skola24Schedule.organizationExport.description.paragraph1', { ns: 'configuration' })} </p>
        <p>{t('skola24Schedule.organizationExport.description.paragraph2', { ns: 'configuration' })} </p>
        <p>{t('skola24Schedule.organizationExport.description.paragraph3', { ns: 'configuration' })} </p>
        <div style={{ marginBottom: '8px' }}>
            <Label check>
                <Input type="checkbox" name="organizationExport.enabled" id="organizationExport.enabled" disabled={props.disabled} checked={organizationExportInput.enabled} onChange={(e) => update({ ...organizationExportInput, enabled: e.target.checked })} /> {t('skola24Schedule.organizationExport.enable', { ns: 'configuration' })}
            </Label>
        </div>
        {organizationExportInput.enabled && renderDomainNameFilter()}
    </FormGroup>
}

export default Skola24ScheduleOrganizationExport;
